const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

/**
 * Builds one explainable, business-focused next step for the promotion.
 * Rules remain deterministic so the UI is predictable and auditable.
 */
export function buildOfferNextBestAction(snapshot = {}, links = {}) {
  const inviteCount = num(snapshot.inviteCount);
  const activeInviteCount = num(snapshot.activeInviteCount);
  const acceptedInviteCount = num(snapshot.acceptedInviteCount);
  const onboardingSubmittedCount = num(snapshot.onboardingSubmittedCount);
  const clicks = num(snapshot.clicksCount ?? snapshot.clicks);
  const leadCount = num(snapshot.leadCount ?? snapshot.leads);
  const bookingRequestCount = num(
    snapshot.bookingRequestCount ?? snapshot.bookingRequests,
  );
  const confirmedBookingCount = num(
    snapshot.confirmedBookingCount ?? snapshot.confirmedBookings,
  );
  const completedBookingCount = num(
    snapshot.completedBookingCount ?? snapshot.completedBookings,
  );
  const noShowBookingCount = num(
    snapshot.noShowBookingCount ?? snapshot.noShowBookings,
  );
  const reachedConfirmationCount = Math.min(
    bookingRequestCount || Number.POSITIVE_INFINITY,
    confirmedBookingCount +
      completedBookingCount +
      noShowBookingCount,
  );
  const redemptionCount = num(snapshot.redemptionCount);
  const spend = num(snapshot.spendValue ?? snapshot.spend ?? snapshot.cost);
  const revenue = num(snapshot.revenueValue ?? snapshot.revenue);
  const roi = Number(snapshot.roiValue ?? snapshot.roiMultiple ?? snapshot.roi);

  if (inviteCount === 0) {
    return {
      id: "add-creators",
      priority: 100,
      eyebrow: "Recommended next step",
      title: "Choose creators for this promotion",
      description:
        "Start with a small group of relevant local creators and prepare one tracked promotion link for each person.",
      note: "A focused creator list is easier to manage and measure.",
      primaryAction: links.creators
        ? { label: "Choose creators", to: links.creators }
        : null,
      evidence: { inviteCount },
    };
  }

  if (acceptedInviteCount > onboardingSubmittedCount) {
    const pending = acceptedInviteCount - onboardingSubmittedCount;
    return {
      id: "collect-creator-details",
      priority: 95,
      eyebrow: "Recommended next step",
      title: `Collect details from ${pending} creator${pending === 1 ? "" : "s"}`,
      description:
        "They have agreed to take part but have not completed the details needed to run the promotion.",
      note: "Open creator outreach and share the onboarding link where needed.",
      primaryAction: links.creators
        ? { label: "Review creator outreach", to: links.creators }
        : null,
      evidence: { acceptedInviteCount, onboardingSubmittedCount },
    };
  }

  if (bookingRequestCount > reachedConfirmationCount) {
    const pending = bookingRequestCount - reachedConfirmationCount;
    return {
      id: "review-bookings",
      priority: 92,
      eyebrow: "Customer action required",
      title: `Review ${pending} booking request${pending === 1 ? "" : "s"}`,
      description:
        "Customers have requested a date and time. Respond promptly so interest is not lost.",
      note: "Confirm availability or decline the request so the customer knows what happens next.",
      primaryAction: links.bookings
        ? { label: "Review bookings", to: links.bookings }
        : null,
      evidence: {
        bookingRequestCount,
        confirmedBookingCount,
        completedBookingCount,
        noShowBookingCount,
        reachedConfirmationCount,
      },
    };
  }

  if (leadCount > 0 && bookingRequestCount === 0) {
    return {
      id: "follow-up-leads",
      priority: 90,
      eyebrow: "Recommended next step",
      title: "Follow up with interested customers",
      description:
        "Customer details have been captured, but no booking requests have been recorded yet.",
      note: "Prioritise recent leads and customers who supplied a phone number.",
      primaryAction: links.bookings
        ? { label: "Open campaign customers", to: links.bookings }
        : links.roi
          ? { label: "Review customer results", to: links.roi }
          : null,
      evidence: { leadCount, bookingRequestCount },
    };
  }

  if (clicks > 0 && leadCount === 0) {
    return {
      id: "improve-conversion",
      priority: 88,
      eyebrow: "Performance insight",
      title: "Turn more visits into customer leads",
      description:
        "People are opening the promotion, but no customer details have been captured. Review the offer value, page clarity and call to action.",
      note: `${clicks} tracked visit${clicks === 1 ? "" : "s"} recorded so far.`,
      primaryAction: links.roi
        ? { label: "Review customer results", to: links.roi }
        : null,
      evidence: { clicks, leadCount },
    };
  }

  if (confirmedBookingCount > 0) {
    const pending = confirmedBookingCount;
    return {
      id: "complete-bookings",
      priority: 85,
      eyebrow: "Recommended next step",
      title: `Update ${pending} confirmed booking${pending === 1 ? "" : "s"}`,
      description:
        "Mark completed visits, cancellations or no-shows so campaign results remain accurate.",
      primaryAction: links.bookings
        ? { label: "Update bookings", to: links.bookings }
        : null,
      evidence: { confirmedBookingCount, completedBookingCount },
    };
  }

  if (redemptionCount === 0 && completedBookingCount === 0) {
    return {
      id: "start-outreach",
      priority: 80,
      eyebrow: "Recommended next step",
      title: "Start creator outreach",
      description:
        "The promotion is ready. Send the prepared creator messages and tracked links to begin generating customer activity.",
      note:
        activeInviteCount > 0
          ? `${activeInviteCount} creator record${activeInviteCount === 1 ? " is" : "s are"} active.`
          : "Use the creator-specific link in every message.",
      primaryAction: links.creators
        ? { label: "Open creator outreach", to: links.creators }
        : null,
      evidence: { inviteCount, activeInviteCount },
    };
  }

  if (spend <= 0 && (revenue > 0 || completedBookingCount > 0 || redemptionCount > 0)) {
    return {
      id: "complete-roi-inputs",
      priority: 70,
      eyebrow: "Recommended next step",
      title: "Add campaign spend to calculate ROI",
      description:
        "Customer outcomes are available, but return on investment needs the promotion cost.",
      note:
        revenue > 0
          ? "Attributed revenue is already available."
          : "Add revenue when customer value is known.",
      primaryAction: links.roi
        ? { label: "Review customer results", to: links.roi }
        : null,
      secondaryAction: links.redemptions
        ? { label: "Record customer outcome", to: links.redemptions, className: "btn btn-ghost" }
        : null,
      evidence: { completedBookingCount, redemptionCount, spend, revenue },
    };
  }

  return {
    id: "review-performance",
    priority: 60,
    eyebrow: "Recommended next step",
    title: "Review customer acquisition results",
    description:
      "Compare customer leads, booking requests, completed outcomes, revenue and creator attribution to decide what to improve next.",
    note: Number.isFinite(roi)
      ? `Current ROI: ${Math.round(roi * 10) / 10}x.`
      : "ROI will appear when spend and revenue data are available.",
    primaryAction: links.roi
      ? { label: "Review customer results", to: links.roi }
      : null,
    evidence: {
      leadCount,
      bookingRequestCount,
      completedBookingCount,
      redemptionCount,
      spend,
      revenue,
      roi,
    },
  };
}
